import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { ref, get, query, orderByChild, limitToLast, push, update } from 'firebase/database';
import { realtimeDb } from '../firebase/config';
import { FaStar, FaThumbsUp, FaThumbsDown, FaUser, FaCalendar } from 'react-icons/fa';
import './ProductReviews.css';

const ProductReviews = ({ productId, productName }) => {
  const { currentUser } = useAuth();
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [showReviewForm, setShowReviewForm] = useState(false);
  const [formData, setFormData] = useState({
    rating: 5,
    title: '',
    comment: '',
    pros: '',
    cons: ''
  });
  const [errors, setErrors] = useState({});

  // Rate limiting state
  const [lastReviewTime, setLastReviewTime] = useState(0);
  const RATE_LIMIT_MS = 60000; // 1 minute between reviews

  const loadReviews = useCallback(async () => {
    try {
      const reviewsRef = ref(realtimeDb, `reviews/${productId}`);
      const reviewsQuery = query(reviewsRef, orderByChild('createdAt'), limitToLast(50));
      
      const snapshot = await get(reviewsQuery);
      if (snapshot.exists()) {
        const reviewsData = snapshot.val();
        const reviewsArray = Object.entries(reviewsData).map(([key, review]) => ({
          id: key,
          ...review,
          createdAt: new Date(review.createdAt)
        }));
        setReviews(reviewsArray.sort((a, b) => b.createdAt - a.createdAt));
      } else {
        setReviews([]);
      }
    } catch (error) {
      console.error('Error loading reviews:', error);
    } finally {
      setLoading(false);
    }
  }, [productId]);

  useEffect(() => {
    loadReviews();
  }, [loadReviews]);

  const validateForm = () => {
    const newErrors = {};
    
    // Input validation and sanitization
    if (!formData.title.trim()) {
      newErrors.title = 'Review title is required';
    } else if (formData.title.trim().length < 5) {
      newErrors.title = 'Title must be at least 5 characters';
    } else if (formData.title.trim().length > 100) {
      newErrors.title = 'Title must be less than 100 characters';
    }

    if (!formData.comment.trim()) {
      newErrors.comment = 'Review comment is required';
    } else if (formData.comment.trim().length < 10) {
      newErrors.comment = 'Comment must be at least 10 characters';
    } else if (formData.comment.trim().length > 1000) {
      newErrors.comment = 'Comment must be less than 1000 characters';
    }

    // Rate limiting check
    const now = Date.now();
    if (now - lastReviewTime < RATE_LIMIT_MS) {
      newErrors.general = 'Please wait before submitting another review';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const sanitizeInput = (input) => {
    // Basic XSS prevention
    return input
      .replace(/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi, '')
      .replace(/<[^>]*>/g, '')
      .trim();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!currentUser) {
      alert('Please login to submit a review');
      return;
    }

    if (!validateForm()) {
      return;
    }

    setSubmitting(true);
    try {
      const sanitizedData = {
        title: sanitizeInput(formData.title),
        comment: sanitizeInput(formData.comment),
        pros: sanitizeInput(formData.pros),
        cons: sanitizeInput(formData.cons),
        rating: Math.min(Math.max(formData.rating, 1), 5), // Ensure rating is between 1-5
        userId: currentUser.uid,
        userName: currentUser.displayName || currentUser.email,
        userEmail: currentUser.email,
        productId,
        productName,
        createdAt: Date.now(),
        helpful: 0,
        notHelpful: 0,
        verified: false // Will be set to true if user purchased the product
      };

      const reviewsRef = ref(realtimeDb, `reviews/${productId}`);
      await push(reviewsRef, sanitizedData);

      // Update rate limiting
      setLastReviewTime(Date.now());

      // Reset form
      setFormData({
        rating: 5,
        title: '',
        comment: '',
        pros: '',
        cons: ''
      });
      setShowReviewForm(false);
      setErrors({});

      // Reload reviews
      await loadReviews();

      alert('Review submitted successfully!');
    } catch (error) {
      console.error('Error submitting review:', error);
      setErrors({ general: 'Failed to submit review. Please try again.' });
    } finally {
      setSubmitting(false);
    }
  };

  const handleHelpful = async (reviewId, isHelpful) => {
    if (!currentUser) {
      alert('Please login to rate reviews');
      return;
    }

    try {
      const reviewRef = ref(realtimeDb, `reviews/${productId}/${reviewId}`);
      const snapshot = await get(reviewRef);
      
      if (snapshot.exists()) {
        const review = snapshot.val();
        const updatedReview = {
          ...review,
          [isHelpful ? 'helpful' : 'notHelpful']: (review[isHelpful ? 'helpful' : 'notHelpful'] || 0) + 1
        };
        
        await update(reviewRef, updatedReview);
        await loadReviews(); // Reload to show updated counts
      }
    } catch (error) {
      console.error('Error updating helpful count:', error);
    }
  };

  const averageRating = reviews.length > 0 
    ? reviews.reduce((sum, review) => sum + review.rating, 0) / reviews.length 
    : 0;

  const renderStars = (rating, interactive = false, onChange = null) => {
    return (
      <div className="stars">
        {[1, 2, 3, 4, 5].map((star) => (
          <FaStar
            key={star}
            className={`star ${star <= rating ? 'filled' : ''} ${interactive ? 'interactive' : ''}`}
            onClick={() => interactive && onChange && onChange(star)}
          />
        ))}
      </div>
    );
  };

  if (loading) {
    return <div className="reviews-loading">Loading reviews...</div>;
  }

  return (
    <div className="product-reviews">
      <div className="reviews-header">
        <h3>Customer Reviews</h3>
        <div className="reviews-summary">
          <div className="average-rating">
            <span className="rating-number">{averageRating.toFixed(1)}</span>
            {renderStars(Math.round(averageRating))}
            <span className="review-count">({reviews.length} reviews)</span>
          </div>
          {currentUser && (
            <button 
              className="write-review-btn"
              onClick={() => setShowReviewForm(!showReviewForm)}
            >
              Write a Review
            </button>
          )}
        </div>
      </div>

      {showReviewForm && (
        <form className="review-form" onSubmit={handleSubmit}>
          <h4>Write Your Review</h4>
          
          <div className="form-group">
            <label>Rating *</label>
            <div className="rating-input">
              {renderStars(formData.rating, true, (rating) => 
                setFormData(prev => ({ ...prev, rating }))
              )}
            </div>
          </div>

          <div className="form-group">
            <label>Review Title *</label>
            <input
              type="text"
              value={formData.title}
              onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
              placeholder="Summarize your experience"
              maxLength={100}
            />
            {errors.title && <span className="error">{errors.title}</span>}
          </div>

          <div className="form-group">
            <label>Review Comment *</label>
            <textarea
              value={formData.comment}
              onChange={(e) => setFormData(prev => ({ ...prev, comment: e.target.value }))}
              placeholder="Share your detailed experience with this product"
              maxLength={1000}
              rows={4}
            />
            {errors.comment && <span className="error">{errors.comment}</span>}
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>Pros</label>
              <input
                type="text"
                value={formData.pros}
                onChange={(e) => setFormData(prev => ({ ...prev, pros: e.target.value }))}
                placeholder="What you liked"
                maxLength={200}
              />
            </div>
            <div className="form-group">
              <label>Cons</label>
              <input
                type="text"
                value={formData.cons}
                onChange={(e) => setFormData(prev => ({ ...prev, cons: e.target.value }))}
                placeholder="What could be improved"
                maxLength={200}
              />
            </div>
          </div>

          {errors.general && <div className="error general">{errors.general}</div>}

          <div className="form-actions">
            <button type="button" onClick={() => setShowReviewForm(false)}>
              Cancel
            </button>
            <button type="submit" disabled={submitting}>
              {submitting ? 'Submitting...' : 'Submit Review'}
            </button>
          </div>
        </form>
      )}

      <div className="reviews-list">
        {reviews.length === 0 ? (
          <p className="no-reviews">No reviews yet. Be the first to review this product!</p>
        ) : (
          reviews.map((review) => (
            <div key={review.id} className="review-item">
              <div className="review-header">
                <div className="reviewer-info">
                  <FaUser className="user-icon" />
                  <span className="reviewer-name">{review.userName}</span>
                  {review.verified && <span className="verified-badge">Verified Purchase</span>}
                </div>
                <div className="review-meta">
                  {renderStars(review.rating)}
                  <span className="review-date">
                    <FaCalendar />
                    {review.createdAt.toLocaleDateString()}
                  </span>
                </div>
              </div>

              <div className="review-content">
                <h5 className="review-title">{review.title}</h5>
                <p className="review-comment">{review.comment}</p>
                
                {(review.pros || review.cons) && (
                  <div className="review-pros-cons">
                    {review.pros && (
                      <div className="pros">
                        <strong>Pros:</strong> {review.pros}
                      </div>
                    )}
                    {review.cons && (
                      <div className="cons">
                        <strong>Cons:</strong> {review.cons}
                      </div>
                    )}
                  </div>
                )}
              </div>

              <div className="review-actions">
                <button 
                  onClick={() => handleHelpful(review.id, true)}
                  className="helpful-btn"
                >
                  <FaThumbsUp /> Helpful ({review.helpful || 0})
                </button>
                <button 
                  onClick={() => handleHelpful(review.id, false)}
                  className="not-helpful-btn"
                >
                  <FaThumbsDown /> Not Helpful ({review.notHelpful || 0})
                </button>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default ProductReviews; 